import type { SelectProps, SelectChangeEvent, SxProps } from '@mui/material';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MUISelect,
} from '@mui/material';
import { useState } from 'react';

interface Props<T> {
  sx?: SxProps;
  selectProps?: SelectProps;
  error?: boolean;
  options: { value: string; label: string }[];
  label: string;
  value: T;
  onChange: (v: T) => void;
  onBlur?: () => void;
}

export function Select<T>({
  sx,
  selectProps,
  error,
  options,
  label,
  value,
  onChange,
  onBlur,
}: Props<T>) {
  const [uniqueIdPrefix] = useState(`selectLabel${Math.random()}`);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    onChange(event.target.value as T);
  };

  return (
    <FormControl fullWidth sx={sx}>
      <InputLabel
        id={uniqueIdPrefix}
        sx={{
          fontSize: '0.875rem',
          transform: value
            ? 'translate(16px, -7px) scale(0.75)'
            : 'translate(16px, 10px) scale(1)',

          '&.Mui-focused': {
            transform: 'translate(16px, -7px) scale(0.75)',
          },
        }}
      >
        {label}
      </InputLabel>
      <MUISelect
        {...selectProps}
        labelId={uniqueIdPrefix}
        label={label}
        error={error}
        sx={{
          ...selectProps?.sx,
          borderRadius: 2,

          '& .MuiSelect-select.MuiInputBase-input': {
            pl: 2,
          },

          '& .MuiOutlinedInput-notchedOutline > legend': {
            ml: 0.5,
            fontSize: '0.625rem',
          },
        }}
        MenuProps={{
          ...selectProps?.MenuProps,
          MenuListProps: {
            disablePadding: true,
          },
          slotProps: {
            ...selectProps?.MenuProps?.slotProps,
            paper: {
              sx: {
                borderRadius: 2,
              },
            },
          },
        }}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
      >
        {options.map(({ value, label }) => {
          return (
            <MenuItem
              key={value as string}
              sx={{
                py: 1,
              }}
              value={value as string}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
                title={label}
              >
                {label}
              </Box>
            </MenuItem>
          );
        })}
      </MUISelect>
    </FormControl>
  );
}
